import _ from 'lodash';
import { getUser } from '~src/user';
import { addCanvasImage } from '~core/image';
import { generateObjRefId } from '~core';
import { getState, modifyState } from '~src/state';
import {
  getObjectCenterCoordinates,
  changeImageSrc,
  getImageDimensionsFromUrl,
} from '~src/utils';
import { isLoggedUserGlobalEditor } from '~src/user';
import { EdgeResizer } from '~src/utils';
import {
  EDITOR_MODE_DEFAULT,
  EDITOR_MODE_GLOBAL_TEMPLATE_MANAGER,
  EDITOR_MODE_ORG_TEMPLATE_MANAGER,
} from '~config/editorConstants';

// Varianti di logo disponibili per ciascun tipo di entità (owner).
// Il timbro NON esiste sulle sedi (office).
const allowedLogoTypesByOwner = {
  org: ['logo', 'logo_completo', 'logo_sito', 'timbro'],
  company: ['logo', 'logo_completo', 'logo_sito', 'timbro'],
  office: ['logo', 'logo_completo', 'logo_sito'],
};

// Etichette leggibili delle varianti, mostrate nella sidebar.
const variantLabels = {
  logo: 'Logo',
  logo_completo: 'Logo completo',
  logo_sito: 'Logo sito',
  timbro: 'Timbro',
};

const templatePlaceholderLabels = {
  org: 'Organizzazione',
  company: 'Azienda del defunto',
  office: 'Sede del defunto',
};

// Catena di fallback "a risalita" per ciascun ruolo: se l'entità del ruolo
// richiesto non ha la variante, si risale verso l'organizzazione.
const ownerFallbackChain = {
  office: ['office', 'company', 'org'],
  company: ['company', 'org'],
  org: ['org'],
};

// Loghi legacy (senza logoOwnerType) usavano la cascata completa office -> company -> org.
const LEGACY_FALLBACK_CHAIN = ['office', 'company', 'org'];

const isAllowedLogoType = (ownerType, logoType) =>
  (allowedLogoTypesByOwner[ownerType] || []).includes(logoType);

// Mappa ruolo -> entità sorgente, ricavata dal contesto (utente + defunto selezionato).
const getOwnerSources = ({ user, servizio }) => ({
  org: _.get(user, 'org_pointer'),
  company:
    _.get(servizio, 'company_pointer') ||
    _.get(servizio, 'office_pointer.company_pointer'),
  office: _.get(servizio, 'office_pointer'),
});

// Risolve l'URL del logo per (ruolo, variante) dal contesto, con fallback:
// prima si cerca la variante richiesta risalendo la catena di owner, poi il
// logo base. Per i loghi legacy (ownerType assente) si usa la cascata completa.
const getLogoUrlFromSources = ({ ownerType, logoType, sources }) => {
  const owners = ownerFallbackChain[ownerType] || LEGACY_FALLBACK_CHAIN;
  const variants = logoType === 'logo' ? ['logo'] : [logoType, 'logo'];

  for (const variant of variants) {
    for (const owner of owners) {
      const url = _.get(sources[owner], `${variant}.url`);
      if (url) {
        return url;
      }
    }
  }

  return undefined;
};

const resolveImageUrlWithDimensions = async (url, height) => {
  const resizedUrl = new EdgeResizer(url).scaleToHeight(height);
  const candidateUrls = _.uniq([resizedUrl, url]);
  let lastError;

  for (const candidateUrl of candidateUrls) {
    try {
      const dimensions = await getImageDimensionsFromUrl(candidateUrl);
      return {
        url: candidateUrl,
        ...dimensions,
      };
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError;
};

const buildSidebarItem = ({
  ownerType,
  ownerId,
  ownerLabel,
  logoType,
  url,
}) => ({
  objectId: `${ownerType}:${ownerId}:${logoType}`,
  ownerType,
  ownerId,
  ownerLabel,
  logoType,
  variantLabel: variantLabels[logoType] || logoType,
  url,
});

// Costruisce gli item della sidebar per una singola entità, una per variante presente.
const buildEntityLogos = ({ ownerType, ownerId, ownerLabel, source }) => {
  if (!source) {
    return [];
  }

  return allowedLogoTypesByOwner[ownerType]
    .map((logoType) => {
      const url = _.get(source, `${logoType}.url`);
      return url
        ? buildSidebarItem({ ownerType, ownerId, ownerLabel, logoType, url })
        : null;
    })
    .filter(Boolean);
};

const buildTemplatePlaceholderLogos = ({ sources, fallbackUrl }) =>
  ['org', 'company', 'office'].flatMap((ownerType) =>
    allowedLogoTypesByOwner[ownerType].map((logoType) =>
      buildSidebarItem({
        ownerType,
        ownerId: ownerType,
        ownerLabel: templatePlaceholderLabels[ownerType],
        logoType,
        url:
          getLogoUrlFromSources({ ownerType, logoType, sources }) ||
          fallbackUrl,
      })
    )
  );

const getCompanyLabel = (company) =>
  `Azienda · ${company.alias || company.nome || ''}`.trim();

const getOfficeLabel = (office) => `Sede · ${office.name || ''}`.trim();

const getLogosCacheKey = ({
  mode,
  orgId = '',
  companyId = '',
  officeId = '',
}) => {
  if (mode === 'global') {
    return 'global';
  }
  if (mode === EDITOR_MODE_ORG_TEMPLATE_MANAGER) {
    return `org-tm:${orgId}`;
  }
  return `editor:${orgId}:${companyId}:${officeId}`;
};

export const addCanvasBusinessLogo = async (
  logoType = 'logo',
  ownerType = 'org'
) => {
  if (!isAllowedLogoType(ownerType, logoType)) {
    throw new Error('Invalid logo type');
  }
  const { user } = await getUser();
  const servizio = getState('selected.servizio', {});

  const sources = getOwnerSources({ user, servizio });
  const logoUrl = getLogoUrlFromSources({ ownerType, logoType, sources });

  const options = {
    giotto: {
      objRefId: generateObjRefId('logo'),
      logo: true,
      logoType,
      logoOwnerType: ownerType,
    },
  };

  await addCanvasImage(logoUrl, options);
};

export const fetchLogos = async (forceFetch = false) => {
  const editorMode = getState('editorMode', EDITOR_MODE_DEFAULT);
  const isGlobalEditor = await isLoggedUserGlobalEditor();
  const isGlobal =
    isGlobalEditor || editorMode === EDITOR_MODE_GLOBAL_TEMPLATE_MANAGER;

  const cachedLogos = getState('sidebarLogos');
  const logos = [];
  let cacheKey;
  const logoPlaceholderUrl = `${process.env.OFC_CDN_URL}/logos/ofc_blu.png`;

  if (isGlobal) {
    cacheKey = getLogosCacheKey({ mode: 'global' });
    if (cachedLogos?.cacheKey === cacheKey && !forceFetch) {
      return cachedLogos;
    }

    logos.push(
      ...buildTemplatePlaceholderLogos({
        sources: {},
        fallbackUrl: logoPlaceholderUrl,
      })
    );
  } else {
    const { user } = await getUser({}, forceFetch);
    const orgPointer = _.get(user, 'org_pointer');
    const orgId = _.get(orgPointer, 'objectId', '');

    if (editorMode === EDITOR_MODE_ORG_TEMPLATE_MANAGER) {
      cacheKey = getLogosCacheKey({ mode: editorMode, orgId });
      if (cachedLogos?.cacheKey === cacheKey && !forceFetch) {
        return cachedLogos;
      }

      logos.push(
        ...buildTemplatePlaceholderLogos({
          sources: { org: orgPointer },
          fallbackUrl: logoPlaceholderUrl,
        })
      );
    } else {
      const servizio = getState('selected.servizio', {});
      const company =
        _.get(servizio, 'company_pointer') ||
        _.get(servizio, 'office_pointer.company_pointer');
      const office = _.get(servizio, 'office_pointer');

      cacheKey = getLogosCacheKey({
        mode: editorMode,
        orgId,
        companyId: _.get(company, 'objectId', ''),
        officeId: _.get(office, 'objectId', ''),
      });
      if (cachedLogos?.cacheKey === cacheKey && !forceFetch) {
        return cachedLogos;
      }

      logos.push(
        ...buildEntityLogos({
          ownerType: 'org',
          ownerId: orgId,
          ownerLabel: 'Organizzazione',
          source: orgPointer,
        })
      );

      if (company) {
        logos.push(
          ...buildEntityLogos({
            ownerType: 'company',
            ownerId: _.get(company, 'objectId', ''),
            ownerLabel: getCompanyLabel(company),
            source: company,
          })
        );
      }

      if (office) {
        logos.push(
          ...buildEntityLogos({
            ownerType: 'office',
            ownerId: _.get(office, 'objectId', ''),
            ownerLabel: getOfficeLabel(office),
            source: office,
          })
        );
      }
    }
  }

  modifyState('sidebarLogos', {
    items: logos,
    allowNew: true,
    cacheKey,
  });

  logos.forEach((logo) => {
    getImageDimensionsFromUrl(logo.url)
      .then(({ width, height }) => {
        logo.width = width;
        logo.height = height;
      })
      .catch(() => {
        console.error('Cannot fetch logos. Width and Height not available');
      });
  });

  return {
    items: logos,
    allowNew: true,
    cacheKey,
  };
};

// Ri-risolve, al caricamento del canvas, l'URL di ogni logo presente in pagina
// in base al suo ruolo (org/company/office), leggendolo dal contesto corrente.
// In editor mode il ruolo si risolve sulla company/office DEL DEFUNTO.
// In template-manager non si lega il modello a un'entità specifica: si salva
// solo il ruolo del placeholder e si usa una preview di comodo.
// I loghi legacy (senza logoOwnerType) mantengono la cascata office->company->org.
export const resolveLogosOnCurrentPage = async () => {
  const { user } = await getUser();
  const servizio = getState('selected.servizio', {});
  const editorMode = getState('editorMode', EDITOR_MODE_DEFAULT);

  const isTemplateManager = [
    EDITOR_MODE_ORG_TEMPLATE_MANAGER,
    EDITOR_MODE_GLOBAL_TEMPLATE_MANAGER,
  ].includes(editorMode);

  // In template-manager non c'è defunto: l'anteprima si appoggia agli item
  // semantici calcolati da fetchLogos.
  const sidebarLogos = isTemplateManager ? await fetchLogos() : null;
  const sources = getOwnerSources({ user, servizio });

  const objects = window.canvas.getObjects();

  for (const obj of objects) {
    const isLogo = _.get(obj, 'giotto.logo', false);

    if (!isLogo) {
      continue;
    }

    const ownerType = _.get(obj, 'giotto.logoOwnerType');
    const logoType = _.get(obj, 'giotto.logoType', 'logo');
    delete obj.giotto.logoOwnerId;

    let url;
    if (sidebarLogos) {
      const match = sidebarLogos.items.find(
        (item) => item.ownerType === ownerType && item.logoType === logoType
      );
      url =
        match?.url ||
        getLogoUrlFromSources({ ownerType: 'org', logoType, sources });
    } else {
      url = getLogoUrlFromSources({ ownerType, logoType, sources });
    }

    if (!url) {
      continue;
    }

    try {
      const targetHeight = obj.height * obj.scaleY;
      const {
        url: resolvedUrl,
        width,
        height,
      } = await resolveImageUrlWithDimensions(url, targetHeight);
      const center = getObjectCenterCoordinates(obj);
      const displayScale = height ? targetHeight / height : 1;

      await changeImageSrc(obj, resolvedUrl);

      obj.scaleToHeight(targetHeight);
      obj.left = center.x - (width * displayScale) / 2;
      obj.top = center.y - targetHeight / 2;
      obj.setCoords();
    } catch (error) {
      console.error('Cannot resolve logo on current page', error);
    }
  }
  window.canvas.renderAll();
};
